import { memo, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { dequal } from "dequal";
import type { AppDispatch, RootState } from "@/store/store";
import { fetchFiltersByMarketId } from "@/store/slices/filters/filters.thunk";
import { fetchSegmentsByMarketId } from "@/store/slices/segments/segments.thunk";
import { fetchAttributeLabels } from "@/store/slices/attributeLabels/attributeLabels.thunk";
import {
  markFiltersReady,
  markSegmentsReady,
  setPendingValidationSegments,
} from "@/store/slices/pendingSelection/pendingSelectionSlice";
import { ROUTES_CHILDREN_LOOKUP } from "@/routes/routes";
import {
  MARKETS,
  POINT_IN_TIME,
  SEGMENTS,
} from "@/constant/constantsThrougoutProject";

/**
 * FetchFiltersAndSegments
 *
 * Renderless component responsible for:
 *
 *  1. **Initial fetch** — waits for market data to be available in
 *     `activeGroup`, then determines the initial market ID to fetch against:
 *       • If the URL already contains a `markets` param, the first valid ID
 *         from that list is used.
 *       • Otherwise the US-first default (same sort as `buildActiveGroupSnapshot`)
 *         is used.
 *     After both fetches complete it dispatches `markSegmentsReady()` /
 *     `markFiltersReady()` so the `selectionListener` can proceed with its
 *     prune cycle.
 *
 *  2. **Market-change re-fetch** — after the initial fetch is done, watches
 *     `userSelection.markets`.  When the selected market IDs genuinely change
 *     (i.e. the user picks a different market — NOT the initial listener
 *     commit that goes from `[]` to a real value) it re-fetches segments +
 *     filters with the new IDs.
 *
 * Replaces `RefreshFilterAndSegmentOnMarketChange`.
 * Must be rendered inside the Router tree (requires `useLocation`).
 */
const FetchFiltersAndSegments: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();

  // ── Store subscriptions ─────────────────────────────────────────────────

  /** All market IDs in the active group (populated after bootstrap). */
  const allMarketIds = useSelector(
    (state: RootState) =>
      (state.activeGroup.markets as any).markets.ids as number[],
    dequal,
  );

  /** Market entities map — needed to resolve identity / market code. */
  const marketsEntities = useSelector(
    (state: RootState) =>
      (state.activeGroup.markets as any).markets.entities as Record<
        number,
        { identity: string }
      >,
    dequal,
  );

  /** Readiness flags — set/reset by the listener and this component. */
  const filtersReady = useSelector(
    (state: RootState) => state.pendingSelection.filtersReady,
  );
  const segmentsReady = useSelector(
    (state: RootState) => state.pendingSelection.segmentsReady,
  );

  /**
   * Markets currently staged in the pending selection.
   * After a market change the listener resets the flags and leaves
   * `pendingSelection.markets` intact (it hasn't committed yet), so we
   * can read the new market IDs from here.
   */
  const pendingMarkets = useSelector(
    (state: RootState) => state.pendingSelection.markets,
    dequal,
  );

  /** Current mode — needed to resolve the correct filter module. */
  const userSelectedMode = useSelector(
    (state: RootState) => state.userSelection.mode,
  );

  // ── Refs ────────────────────────────────────────────────────────────────

  /** Becomes `true` after the initial segments + filters fetch has been
   *  kicked off so the effect never fires twice. */
  const hasInitializedRef = useRef(false);

  /**
   * Counts how many `fetchForMarkets` calls (with `shouldSetFlags=true`) are
   * currently in-flight.  `markSegmentsReady()` is only dispatched when this
   * reaches **zero**, ensuring the selectionListener is not unblocked until
   * the *last* concurrent fetch completes.
   *
   * This matters on initial page-load where Effect 1 and Effect 2 both fire
   * near-simultaneously: Effect 1 fetches for only the first URL market while
   * Effect 2 (triggered by `useInitialURLBootstrap`) fetches for **all** URL
   * markets.  Effect 2's result is the authoritative dataset for
   * `captureUnknownSegments`; delaying the ready signal until Effect 2 finishes
   * guarantees `ValidateSegment` sees the correct `pendingValidationSegments`.
   */
  const pendingFetchCountRef = useRef(0);

  // ── Helpers ─────────────────────────────────────────────────────────────

  /** Derive market codes (identity strings) from a list of market IDs. */
  const getMarketCodes = (ids: number[]): string[] =>
    ids.map((id) => marketsEntities[id]?.identity).filter(Boolean) as string[];

  /**
   * Resolve the `module` parameter for `fetchFiltersByMarketId`.
   *
   * Routes whose `multipleOptionsForKeys` include `"markets"` for the active
   * mode use the `"GLOBAL"` module; all others use `"COMPETITIVE"`.
   */
  const getFilterModule = (): string => {
    const routeDetail = ROUTES_CHILDREN_LOOKUP[location.pathname];
    return routeDetail?.multipleOptionsForKeys?.[
      userSelectedMode || POINT_IN_TIME
    ]?.includes(MARKETS)
      ? "GLOBAL"
      : "COMPETITIVE";
  };

  /**
   * Fetch segments and filters for the given market IDs.
   *
   * @param marketIdsToUse          The resolved market IDs to pass to the thunks.
   * @param shouldSetFlags          When `true`, dispatches `markSegmentsReady` /
   *                                `markFiltersReady` after each fetch so the
   *                                listener's `condition` guard can proceed.
   * @param captureUnknownSegments  When `true` (initial page-load only), compares
   *                                the URL's `segments` params against the freshly
   *                                loaded segment IDs and stores any unknowns in
   *                                `pendingValidationSegments` for `ValidateSegment`
   *                                to validate — before the listener prunes the URL.
   */
  async function fetchForMarkets(
    marketIdsToUse: number[],
    shouldSetFlags: boolean,
    captureUnknownSegments: boolean = false,
  ): Promise<void> {
    // Increment synchronously (before the first `await`) so the counter
    // accurately reflects every in-flight call when any one of them reaches
    // its `finally` block.  markSegmentsReady() is held back until the
    // counter returns to zero — i.e. the last concurrent fetch has finished.
    if (shouldSetFlags) {
      pendingFetchCountRef.current += 1;
    }

    const marketCodes = getMarketCodes(marketIdsToUse);

    // ── Segments ───────────────────────────────────────────────────────────
    // NOTE: Do NOT dispatch setPendingSelection for segments here.
    // Doing so would:
    //   1. Replace the in-flight `markets` field in pendingSelection (losing the new market ID)
    //   2. Spawn a new listener instance that calls cancelActiveListeners(), killing the
    //      waiting market-change listener (Listener 1).
    // For SEGMENT_ONLY screens the selectionListener's pruneInvalidFields already
    // seeds out.segments = [...snapshot.activeSegmentIds] once both ready flags are
    // true and the store has been populated by fetchSegmentsByMarketId below.
    try {
      const fetchedSegments = await dispatch(
        fetchSegmentsByMarketId({ marketIds: marketIdsToUse, marketCodes }),
      ).unwrap();

      // ── Capture unknown segments (initial load on /segment paths only) ──
      // Runs only after a SUCCESSFUL fetch. We derive the set of user-owned
      // segment IDs directly from the dispatch response (fetchedSegments) so
      // that we are guaranteed to have the latest data regardless of whether
      // the Redux store has been synchronously updated yet.
      // Must run BEFORE markSegmentsReady() so the selectionListener is still
      // blocked and hasn't pruned the URL yet.
      if (captureUnknownSegments && fetchedSegments?.segments?.length > 0) {
        // Build the set of segment IDs the user already has in their library
        // using the unwrapped dispatch result rather than store.getState().
        const userSegmentIds = new Set<number>(
          (fetchedSegments.segments ?? []).map((s) => s.id),
        );
        const urlSegments = new URLSearchParams(window.location.search)
          .getAll(SEGMENTS)
          .map(Number)
          .filter(Boolean);
        // Only keep URL segment IDs that are NOT already in the user's library.
        const unknownSegments = urlSegments.filter(
          (id) => !userSegmentIds.has(id),
        );
        // Always dispatch (even when empty) so a later call with more complete
        // market data can overwrite a preliminary result from an earlier call.
        dispatch(setPendingValidationSegments(unknownSegments));
      }
    } catch {
      // Fetch failed — skip captureUnknownSegments entirely.
      // We have no reliable view of which segments the user already owns,
      // so storing URL segments as pending validation could include ones
      // they already have.  The listener is still unblocked by
      // markSegmentsReady() in the finally block below.
    } finally {
      if (shouldSetFlags) {
        pendingFetchCountRef.current -= 1;
        // Only unblock the selectionListener when ALL concurrent fetches have
        // finished — the last one to complete holds the most complete data set
        // and its captureUnknownSegments result is the one ValidateSegment uses.
        if (pendingFetchCountRef.current === 0) {
          dispatch(markSegmentsReady());
        }
      }
    }

    // ── Attribute labels (fire-and-forget, no awaiting needed) ─────────────
    dispatch(fetchAttributeLabels({ marketIds: marketIdsToUse }));

    // ── Filters ────────────────────────────────────────────────────────────
    try {
      await dispatch(
        fetchFiltersByMarketId({
          marketIds: marketIdsToUse,
          module: getFilterModule(),
        }),
      );
    } catch {
      // Fetch failed — readiness flag dispatched below regardless.
    } finally {
      if (shouldSetFlags) {
        dispatch(markFiltersReady());
      }
    }
  }

  // ── Effect 1: Initial fetch when markets become available ───────────────

  useEffect(() => {
    // Wait until market data is available; guard against re-running.
    if (allMarketIds.length === 0 || hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    // Determine which market ID to use for the initial fetch.
    // Priority: valid IDs from the URL → US-first default.
    const searchParams = new URLSearchParams(window.location.search);
    const urlMarketIds = searchParams
      .getAll("markets")
      .map(Number)
      .filter(Boolean);

    const validUrlMarkets = urlMarketIds.filter((id) =>
      allMarketIds.includes(id),
    );

    let targetMarketIds: number[];

    if (validUrlMarkets.length > 0) {
      // Use the first valid market from the URL (listener will expand/prune later).
      targetMarketIds = [validUrlMarkets[0]];
    } else {
      // Sort markets so that US ones come first — mirrors buildActiveGroupSnapshot.
      const sortedIds = [...allMarketIds].sort((a, b) => {
        const aIsUS = (marketsEntities[a]?.identity ?? "")
          .toUpperCase()
          .startsWith("US");
        const bIsUS = (marketsEntities[b]?.identity ?? "")
          .toUpperCase()
          .startsWith("US");
        if (aIsUS && !bIsUS) return -1;
        if (!aIsUS && bIsUS) return 1;
        return 0;
      });
      targetMarketIds = [sortedIds[0]];
    }

    // Pass captureUnknownSegments=true so that any shared/unknown segment IDs
    // present in the URL are stored in pendingValidationSegments before the
    // selectionListener prunes the URL.
    fetchForMarkets(targetMarketIds, true, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMarketIds]);

  // ── Effect 2: Re-fetch when a market-change dispatch resets both flags ───
  //
  // `MarketsDropdownView` includes `filtersReady: false, segmentsReady: false`
  // in the same `setPendingSelection` payload as the new market IDs, so both
  // flags are reset atomically in one action — no separate `resetReadinessFlags`
  // dispatch is needed.  We react to that combined signal here: when both flags
  // are `false` and `pendingSelection` still carries the new market IDs, we
  // re-fetch segments + filters so the waiting listener has fresh data before
  // it runs its prune cycle.

  useEffect(() => {
    // Guard 1: ignore until the initial fetch has been kicked off.
    if (!hasInitializedRef.current) return;

    // Guard 2: proceed only when the listener has reset BOTH flags — that
    // is the signal for a market change.  If at least one flag is still
    // `true` this is not a market-change reset.
    if (filtersReady || segmentsReady) return;

    // Guard 3: we need the new market IDs from the pending selection.
    if (!pendingMarkets || pendingMarkets.length === 0) return;

    // Re-fetch with the new market IDs; set readiness flags when done so
    // the waiting listener can proceed with its prune cycle.
    //
    // captureUnknownSegments=true: on the initial page-load this effect fires
    // simultaneously with Effect 1 (triggered by useInitialURLBootstrap placing
    // ALL URL markets into pendingMarkets).  Because pendingFetchCountRef delays
    // markSegmentsReady() until the last in-flight fetch completes, this call's
    // result — which covers ALL URL markets, not just the first one — is
    // guaranteed to be the final value written to pendingValidationSegments
    // before ValidateSegment is unblocked.
    fetchForMarkets(pendingMarkets, true, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersReady, segmentsReady, pendingMarkets]);

  return null;
};

export default memo(FetchFiltersAndSegments);
